import { getStoryblokApi } from "@storyblok/react";
import { NavigationModel } from "../Models/NavigationModel";
import axios from "axios";

class Utils {
  static getCacheUrl(url: string): string {
    if (url.startsWith("https://a.storyblok.com/")) {
      return url.replace(
        "https://a.storyblok.com/",
        `${window.location.origin}/cache-api/api/storyblok/assets/`
      );
    } else {
      return url;
    }
  }

  static handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
    fallbackUrl?: string
  ) => {
    if (fallbackUrl) {
      const img = e.target as HTMLImageElement;
      if (img.src !== fallbackUrl) {
        img.src = fallbackUrl;
      }
    }
  };

  static handleVideoError = (
    e: React.SyntheticEvent<HTMLVideoElement, Event>,
    fallbackUrl: string
  ) => {
    const video = e.currentTarget;
    const source = video.querySelector("source");

    if (source && source.src !== fallbackUrl) {
      source.src = fallbackUrl;
      video.load();
    }
  };

  static mapResolvedRelations(
    story: StoryblokStory,
    rels: StoryblokStory[]
  ): StoryblokStory {
    const relMap: Record<string, StoryblokStory> = {};
    for (const rel of rels) {
      relMap[rel.uuid] = rel;
    }

    function traverse(node: any): any {
      if (Array.isArray(node)) {
        const isUUIDArray = node.every(
          (item) => typeof item === "string" && relMap[item]
        );

        if (isUUIDArray) {
          return node.map((uuid) => relMap[uuid]);
        }

        return node.map(traverse);
      }

      if (node !== null && typeof node === "object") {
        const newNode: Record<string, any> = { ...node };

        for (const key in newNode) {
          const value = newNode[key];

          if (typeof value === "string" && relMap[value]) {
            newNode[key] = relMap[value];
          } else {
            newNode[key] = traverse(value);
          }
        }

        return newNode;
      }

      return node;
    }

    return {
      ...story,
      content: traverse(story.content),
    };
  }

  static async getCachedOrStoryblokSiteConfig() {
    try {
      return await this.getSiteConfig();
    } catch {
      return await this.getStoryblokSiteConfig();
    }
  }

  static getSiteConfig = () => {
    return axios
      .get(
        `${window.location.origin}/cache-api/api/storyblok/cache/siteconfig`,
        {
          params: {
            version: "published",
            resolve_relations: "SiteConfig.NavigationStrip",
            resolve_level: 2,
          },
        }
      )
      .then((res) => res.data);
  };

  static getStoryblokSiteConfig = () => {
    return getStoryblokApi()
      .get(`cdn/stories/siteconfig`, {
        version: "published",
        resolve_relations: "SiteConfig.NavigationStrip",
      })
      .then((res) => res.data);
  };

  static async getCachedOrStoryblokStory(slug: string) {
    try {
      return await this.getStory(slug);
    } catch {
      return await this.getStoryblokStory(slug);
    }
  }

  static getStory = (slug: string) => {
    // return getStoryblokApi().get(`cdn/stories/${slug}`, {
    //   version: "published",
    //   resolve_relations: [
    //     "GlobalReference.reference",
    //     "ShowcasePanel.entity",
    //     "ListPanel.entities",
    //     "SliderPanelItem.entity",
    //     "SliderPanel.entities",
    //     "Article.people",
    //     "Article.relatedArticles",
    //     "Faq.people",
    //     "Faq.relatedFaqs",
    //   ],
    // });
    return axios
      .get(`${window.location.origin}/cache-api/api/storyblok/cache/${slug}`, {
        params: {
          version: "published",
          resolve_relations:
            "GlobalReference.reference,ShowcasePanel.entity,ListPanel.entities,SliderPanelItem.entity,SliderPanel.SliderPanelItem.entity,SliderPanel.entities,Article.people,Article.relatedArticles,Faq.people,Faq.relatedFaqs",
          resolve_level: 2,
        },
      })
      .then((res) => res.data);
  };

  static getStoryblokStory = (slug: string) => {
    return getStoryblokApi()
      .get(`cdn/stories/${slug}`, {
        version: "published",
        resolve_relations: [
          "GlobalReference.reference",
          "ShowcasePanel.entity",
          "ListPanel.entities",
          "SliderPanelItem.entity",
          "SliderPanel.entities",
          "Article.people",
          "Article.relatedArticles",
          "Faq.people",
          "Faq.relatedFaqs",
        ],
      })
      .then((res) => res.data);
  };

  static getScripts = (
    container: HTMLDivElement,
    script: string
  ): Array<HTMLScriptElement> => {
    container.innerHTML = "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = script;
    let scripts: Array<HTMLScriptElement> = [];

    Array.from(tempDiv.children).forEach((child) => {
      if (child.tagName === "SCRIPT") {
        const scriptElement = child as HTMLScriptElement;
        const newScript = document.createElement("script");
        if (scriptElement.src) {
          newScript.src = scriptElement.src;
          newScript.async = true;
        } else {
          newScript.textContent = child.textContent;
        }
        scripts.push(newScript);
      } else {
        container.appendChild(child.cloneNode(true));
      }
    });
    return scripts;
  };

  static loadScripts = (
    scripts: Array<HTMLScriptElement>,
    containerRef: React.RefObject<HTMLDivElement | null>,
    index: number = 0
  ) => {
    if (index >= scripts.length) return;

    const script = scripts[index];
    if (script.src) {
      script.onload = () => {
        Utils.loadScripts(scripts, containerRef, index + 1);
      };
      containerRef.current?.appendChild(script);
    } else {
      containerRef.current?.appendChild(script);
      Utils.loadScripts(scripts, containerRef, index + 1);
    }
  };

  static getFormattedDate = (item: string) => {
    const date = new Date(item);

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return `${daysOfWeek[date.getUTCDay()]} ${date.getUTCDate()} ${
      months[date.getUTCMonth()]
    } ${date.getUTCFullYear()}`;
  };

  static getDownloadFormattedDate = (dateString: string) => {
    const date = new Date(dateString);

    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "long",
      year: "numeric",
    };
    const formattedDate = date.toLocaleDateString("en-GB", options);

    const day = formattedDate.split(" ")[0];
    let daySuffix;

    switch (day) {
      case "1":
      case "21":
      case "31":
        daySuffix = "st";
        break;
      case "2":
      case "22":
        daySuffix = "nd";
        break;
      case "3":
      case "23":
        daySuffix = "rd";
        break;
      default:
        daySuffix = "th";
    }

    return `${day}${daySuffix} ${formattedDate.slice(2)}`;
  };

  static setNavidation(items: Array<NavigationModel>) {
    const searchParams = new URLSearchParams(window.location.search);
    items.forEach((item: NavigationModel) => {
      searchParams.set(item.param, item.value);
    });
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  }

  static scrollTo(scrollToName: string, scrollToIndex: number) {
    const elements = document.querySelectorAll(scrollToName);
    if (elements.length >= scrollToIndex - 1) {
      const notificationElement = document.querySelector(
        ".notification-strip-nav"
      );
      let notificationHeight = 0;
      if (window.innerWidth > 900) {
        notificationHeight = 33; // For Small navigation
      } else if (notificationElement instanceof HTMLElement) {
        notificationHeight = notificationElement.offsetHeight;
      }
      const elementPosition =
        elements[scrollToIndex - 1].getBoundingClientRect().top +
        window.scrollY;
      window.scrollTo({ top: elementPosition - notificationHeight });
    }
  }
}

export default Utils;

export type StoryblokStory = {
  uuid: string;
  content: any;
  [key: string]: any;
};
